import { useCallback, useRef, useState } from "react";
import { transcribeAudio } from "@/lib/api";
import { notifyStopAllSpeech } from "@/lib/speechBus";
import { toast } from "sonner";

/**
 * Shared recorder hook — one active session, controlled from either the
 * desktop `<VoiceRecorder>` in the page body or the mobile
 * `<SectionBottomBar>` mic pill. Because both surfaces read/write the same
 * state, tapping "stop" on either ends the same recording.
 *
 * Also calls `notifyStopAllSpeech()` on `start()` so a Mirror reply that
 * is currently narrating is instantly hushed the moment the user reaches
 * for the mic — the "voice mid-turn interrupt" behaviour.
 */
export function useRecorder({ onTranscript, transcribe = transcribeAudio }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = useCallback(async () => {
    // Interrupt any Mirror playback the moment the mic is armed.
    notifyStopAllSpeech();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setRecording(false);
        if (blob.size < 800) {
          toast.error("The recording was too short to transcribe.");
          return;
        }
        setTranscribing(true);
        try {
          const text = await transcribe(blob);
          if (text) onTranscript(text);
          else toast.error("No speech was detected.");
        } catch (e) {
          console.error(e);
          toast.error("Transcription failed. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error(e);
      toast.error("Microphone access was denied.");
    }
  }, [onTranscript, transcribe]);

  const stop = useCallback(() => {
    try {
      mediaRef.current?.stop();
    } catch {
      /* MediaRecorder already stopped */
    }
  }, []);

  return { recording, transcribing, start, stop };
}
